import { Component } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { forkJoin, Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { TaskService } from 'src/app/core/services/task.service';
import { TableColumnWithFilter } from 'src/app/shared/components/table/interfaces/table-column';
import { StateFacade } from '../../../facades/state.facade';
import { BulkChangeComponentData } from '../../../interfaces/bulk-change.component';
import { BaseChangeComponent } from '../base-change.component';
import { Task } from 'src/app/core/entity/task';
import { ClientService } from 'src/app/core/services/clients.service';
import { Client } from 'src/app/core/entity/client';
import { UserType } from 'src/app/core/state/types';
import { AppState } from '@app/core/state/appState';
import { Store } from '@ngrx/store';
import { UserSelectors } from 'src/app/core/state/users/users.selectors';
import { CLIENT_RESPONSIBLE } from '@app/shared/misc/constants';

@Component({
  template: `<div [formGroup]="formGroup">
    <p>Vad ska det ändras till?</p>
    <app-user-multi-selector formControlName="toUserIds" placeholder="Välj användare"></app-user-multi-selector>
  </div>`,
})
export class ChangeTaskUserComponent extends BaseChangeComponent {
  public availableUsers$: Observable<UserType[]>;

  constructor(
    stateFacade: StateFacade,
    private taskService: TaskService,
    private clientService: ClientService,
    private store: Store<AppState>,
  ) {
    super(stateFacade);
    this.availableUsers$ = this.store.select(UserSelectors.activeUsersWithoutAll);
  }

  protected load(): Observable<BulkChangeComponentData> {
    this.formGroup.disable({ emitEvent: false });

    return forkJoin([this.taskService.getAllTasks(), this.clientService.getAllArchivedClients()]).pipe(
      map(([tasks, archivedClients]: [Task[], Client[]]) => {
        const archivedClientIds = archivedClients.map((client) => client.id);

        // excludes tasks with archived clients
        return tasks.filter((task: Task) => !archivedClientIds.includes(task.client.id));
      }),
      map((tasks: Task[]) => this.sortTasks(tasks)),
      map((tasks: Task[]) => ({ tableData: tasks, dataToChange: this.formGroup.value })),
      finalize(() => {
        this.formGroup.enable({ emitEvent: false });
      }),
    );
  }

  protected createForm() {
    return new UntypedFormGroup({
      toUserIds: new UntypedFormControl(null, Validators.required),
    });
  }

  protected getDataIdentityKey(): string {
    return 'id';
  }

  protected getTableColumns(): TableColumnWithFilter[] {
    return [
      { field: 'client.name', header: 'Klient', filter: { type: 'text' } },
      { field: 'client.corporateIdentity', header: 'Org.nr', filter: { type: 'text' } },
      { field: 'client.customerNumber', header: 'Kundnr', filter: { type: 'text' } },
      // { field: 'client.type.description', header: 'Typ', filter: { type: 'dropdown', data: [] } },
      {
        field: 'client.responsible',
        header: CLIENT_RESPONSIBLE,
        fieldValue: (responsible) => responsible?.name,
        filter: {
          type: 'multidropdown',
          data: this.availableUsers$,
          dataLabel: 'name',
          dataValue: 'id',
        },
      },
      { field: 'assignmentName', header: 'Uppdrag', filter: { type: 'text' } },
      { field: 'type', header: 'Tjänst', filter: { type: 'text' } },
      {
        field: 'users',
        header: 'Handläggare',
        filter: {
          type: 'multidropdownwithmultirules',
          data: this.availableUsers$,
          dataLabel: 'name',
          dataValue: 'id',
        },
        fieldValue: (users) => users?.map((user: UserType) => user.initials).join(', '),
      },
    ];
  }

  private sortTasks(tasks: Task[]): Task[] {
    return tasks.sort((t1, t2) => {
      if (t1.client.name != t2.client.name) {
        return t1.client.name < t2.client.name ? -1 : 1;
      }
      if (t1.assignmentName != t2.assignmentName) {
        return t1.assignmentName < t2.assignmentName ? -1 : 1;
      }
      return t1.type < t2.type ? -1 : 1;
    });
  }
}
